import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import CatalogImage from "../components/common/CatalogImage";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Tabs from "../components/common/Tabs";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWriteBookings, roleFromProfile } from "../lib/agencyAccess";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { formatCurrency, formatDate, packageNetPrice, packageRsp } from "../lib/catalog";
import { packageSeatSummary } from "../lib/packageSeats";
import {
  FlightPanel,
  GalleryPanel,
  HotelsPanel,
  InclusionsPanel,
  ExclusionsPanel,
  ItineraryPanel,
  OverviewPanel,
  PricingPanel,
} from "../components/packages/PackageDetailSections";
import {
  PACKAGE_DETAIL_TABS,
  displayValue,
  formatDuration,
  getDataShape,
  normalizeFlightLeg,
  normalizeFlightSummary,
  normalizeHotel,
  hotelHasContent,
  normalizeItineraryItem,
} from "../components/packages/packageDetailModel";

async function fetchPackageBySlugOrId(identifier) {
  try {
    return await agentApi.get(`/umrah-packages/slug/${encodeURIComponent(identifier)}`);
  } catch (error) {
    if (error.status === 404 || error.status === 400) {
      return agentApi.get(`/umrah-packages/id/${encodeURIComponent(identifier)}`);
    }
    throw error;
  }
}

export default function PackageDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [canBook, setCanBook] = useState(false);

  useEffect(() => {
    let alive = true;

    async function loadPackage() {
      try {
        const [payload, profile] = await Promise.all([
          fetchPackageBySlugOrId(id),
          fetchCurrentAgent().catch(() => null),
        ]);
        if (!alive) return;
        setData(getDataShape(payload));
        setCanBook(canWriteBookings(roleFromProfile(profile)));
      } catch (fetchError) {
        if (!alive) return;
        showApiError(fetchError, "LOAD_PACKAGE");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadPackage();

    return () => {
      alive = false;
    };
  }, [id]);

  const pkg = data?.package || {};
  const hotels = (data?.hotels || []).map(normalizeHotel).filter(hotelHasContent);
  const itinerary = (data?.itinerary || []).map(normalizeItineraryItem);
  const gallery = data?.gallery || [];
  const flightLegs = (data?.flightBlockDetails || []).map(normalizeFlightLeg);
  const flightSummary = normalizeFlightSummary(pkg, data?.flightBlock, flightLegs);
  const seats = packageSeatSummary(pkg);
  const soldOut = seats.soldOut;
  const bookTo = !soldOut && (pkg.id || pkg.u_pkg_id || id) ? `/packages/${pkg.slug || pkg.id || pkg.u_pkg_id || id}/book` : "";

  return (
    <AgentLayout>
      <div className="container-fluid py-24 agent-package-detail">
        <PageHeader
          eyebrow="Package detail"
          title={pkg.name || "Live package"}
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Packages", href: "/packages" },
            { label: pkg.name || "Package Details" },
          ]}
          backTo="/packages"
          // actions={
          //   bookTo ? (
          //     <Link to={bookTo} className="btn btn-primary btn-sm">
          //       Book Package
          //     </Link>
          //   ) : null
          // }
        />

        {loading ? <PageLoader message="Loading package details..." /> : null}

        {!loading && data ? (
          <>
            <div className="card border-0 shadow-sm mb-4 agent-package-header catalog-card">
              <div className="agent-package-header__grid">
                <CatalogImage folder="umrah_package" fileName={pkg.image || pkg.u_pkg_image} alt={pkg.name} height={280} />
                <div className="card-body">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-2">
                    <div className="d-flex flex-wrap gap-2">
                      <StatusBadge value={displayValue(pkg.code, "No code")} />
                      {soldOut ? <StatusBadge value="Sold Out" /> : null}
                    </div>
                    {canBook && bookTo ? (
                      <Link to={bookTo} className="btn btn-primary btn-sm">
                        Book Package
                      </Link>
                    ) : soldOut ? (
                      <button type="button" className="btn btn-secondary btn-sm" disabled>
                        Sold Out
                      </button>
                    ) : null}
                  </div>
                  <h4 className="mb-3">{pkg.name}</h4>
                  <div className="agent-package-header__facts">
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">Departure</div>
                      <div className="agent-package-fact-value">{displayValue(formatDate(pkg.departureDate), "—")}</div>
                    </div>
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">Return</div>
                      <div className="agent-package-fact-value">{displayValue(formatDate(pkg.returnDate), "—")}</div>
                    </div>
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">Duration</div>
                      <div className="agent-package-fact-value">{displayValue(formatDuration(pkg), "—")}</div>
                    </div>
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">Available seats</div>
                      <div className="agent-package-fact-value">
                        {seats.total ? `${seats.available} / ${seats.total}` : displayValue(pkg.seats ?? pkg.availableSeats, "—")}
                      </div>
                    </div>
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">Package type</div>
                      <div className="agent-package-fact-value">{displayValue(pkg.category || pkg.tourType || pkg.pkg_tour_category, "—")}</div>
                    </div>
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">Net price</div>
                      <div className="agent-package-fact-value">{formatCurrency(packageNetPrice(pkg))}</div>
                    </div>
                    <div className="agent-package-fact">
                      <div className="agent-package-fact-label">RSP</div>
                      <div className="agent-package-fact-value">{formatCurrency(packageRsp(pkg))}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm agent-package-panel">
              <div className="card-header agent-package-tabbar">
                <Tabs
                  tabs={PACKAGE_DETAIL_TABS}
                  activeId={activeTab}
                  onChange={setActiveTab}
                  className="agent-package-tabs"
                  ariaLabel="Package sections"
                  idPrefix="package-tab"
                />
              </div>
              <div className="card-body">
                {activeTab === "overview" ? <OverviewPanel pkg={pkg} /> : null}
                {activeTab === "flight" ? <FlightPanel summary={flightSummary} legs={flightLegs} /> : null}
                {activeTab === "hotels" ? <HotelsPanel hotels={hotels} /> : null}
                {activeTab === "itinerary" ? <ItineraryPanel itinerary={itinerary} /> : null}
                {activeTab === "inclusions" ? <InclusionsPanel pkg={pkg} /> : null}
                {activeTab === "exclusions" ? <ExclusionsPanel pkg={pkg} /> : null}
                {activeTab === "gallery" ? <GalleryPanel gallery={gallery} packageName={pkg.name} /> : null}
                {activeTab === "pricing" ? <PricingPanel pkg={pkg} /> : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
